import { computed, inject, Injectable, signal } from '@angular/core';
import { SupabaseService } from '../../supabase/supabase.service';
import { Database } from '../../supabase/types/database.types';
import { combineLatest, filter, from, map, Observable, switchMap } from 'rxjs';
import { queryMessages } from '../utils/messagesQuery';
import { Message } from '../types/message';
import { AuthService } from '../../auth/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { GalleryPhoto } from '@capacitor/camera';
import { HttpClient } from '@angular/common/http';

export type SendMessageApiRow = Pick<
  Database['public']['Tables']['Messages']['Row'],
  'id' | 'text' | 'localId' | 'createdAt' | 'updatedAt'
>;

export type SendMessageApiResponse = {
  data: SendMessageApiRow[] | null;
  error: { message: string } | null;
  status: number;
};

@Injectable({ providedIn: 'root' })
export class MessagesApiService {
  private supabase = inject(SupabaseService);

  auth = inject(AuthService);
  currentUserData = toSignal(this.auth.profile$);
  currentUser = computed(() => this.currentUserData()?.id);

  http = inject(HttpClient);

  sendMessage(message: Database['public']['Tables']['Messages']['Insert']) {
    return this.http.post<SendMessageApiResponse>('/api/send-message', message);
    //return this.supabase.client.from('Messages').insert(message);
  }
  async loadImage(chat: number, image: GalleryPhoto) {
    const response = await fetch(image.webPath);
    const blob = await response.blob();
    return this.supabase.client.storage
      .from('users_chats_images')
      .upload(
        `${chat}/${this.auth.profileSignal()?.id}_${Date.now()}_file.png`,
        blob,
        {
          cacheControl: '3600',
          upsert: false,
        }
      );
  }

  getMessagesBottom(chatId: number, maxId: number, bufferSize: number) {
    return from(
      this.supabase.client
        .from('Messages')
        .select(queryMessages)
        .eq('chatId', chatId)
        .gt('id', maxId)
        .order('id', { ascending: true })
        .limit(bufferSize)
        .then((res) => {
          return {
            messages: res.data?.sort((a, b) => a.id - b.id) ?? [],
            bottom: (res.data?.length ?? 0) < bufferSize,
          };
        })
    );
  }

  getMessagesTop(chatId: number, minId: number, bufferSize: number) {
    return from(
      this.supabase.client
        .from('Messages')
        .select(queryMessages)
        .eq('chatId', chatId)
        .lt('id', minId)
        .order('id', { ascending: false })
        .limit(bufferSize)
        .then((res) => {
          return {
            messages: res.data?.sort((a, b) => a.id - b.id) ?? [],
            top: (res.data?.length ?? 0) < bufferSize,
          };
        })
    );
  }

  getMessagesAroundMessage(
    chatId: number,
    messageId: number,
    bufferSize: number
  ) {
    return combineLatest({
      bottom: from(
        this.supabase.client
          .from('Messages')
          .select(queryMessages)
          .eq('chatId', chatId)
          .gte('id', messageId)
          .order('id', { ascending: true })
          .limit(bufferSize / 2)
          .then((res) => {
            return {
              messages: res.data?.sort((a, b) => a.id - b.id) ?? [],
              bottom: (res.data?.length ?? 0) < bufferSize / 2,
            };
          })
      ),
      top: from(
        this.supabase.client
          .from('Messages')
          .select(queryMessages)
          .eq('chatId', chatId)
          .lt('id', messageId)
          .order('id', { ascending: false })
          .limit(bufferSize / 2)
          .then((res) => {
            return {
              messages: res.data?.sort((a, b) => a.id - b.id) ?? [],
              top: (res.data?.length ?? 0) < bufferSize / 2,
            };
          })
      ),
    }).pipe(
      map(({ top, bottom }) => {
        return {
          messages: top.messages.concat(bottom.messages),
          top: top.top,
          bottom: bottom.bottom,
        };
      })
    );
  }

  getMessage(id: number) {
    return from(
      this.supabase.client
        .from('Messages')
        .select(queryMessages)
        .eq('id', id)
        .returns<Message[]>()
        .single()
        .then((res) => res.data ?? null)
    );
  }

  getLastMessage(chatId: number) {
    return from(
      this.supabase.client
        .from('Messages')
        .select('id, localId, createdAt')
        .eq('chatId', chatId)
        .order('id', { ascending: false })
        .limit(1)
        .then((res) => res.data?.[0] ?? null)
    );
  }

  listenOtherUserMessages(chatId: number) {
    return new Observable<any>((subscriber) => {
      const sub = this.supabase.client
        .channel('listening-messages-insert-other-' + chatId)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'Messages',
            filter: `chatId=eq.${chatId}`,
          },
          (payload) => {
            subscriber.next(payload.new as Message);
          }
        )
        .subscribe();
      return function unsubscribe() {
        sub.unsubscribe();
      };
    }).pipe(
      filter((m) => m.userId !== this.currentUser()),
      switchMap((m) => this.getMessage(m.id) as Observable<Message>)
    );
  }

  listenMyMessages(chatId: number) {
    return new Observable<any>((subscriber) => {
      const sub = this.supabase.client
        .channel('listening-messages-insert-me-' + chatId)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'Messages',
            filter: `chatId=eq.${chatId}`,
          },
          (payload) => {
            subscriber.next(payload.new);
          }
        )
        .subscribe();
      return function unsubscribe() {
        sub.unsubscribe();
      };
    }).pipe(
      filter((m) => m.userId === this.currentUser()),
      switchMap((m) => this.getMessage(m.id) as Observable<Message>)
    );
  }

  listenUpdateOtherUserMessages(chatId: number) {
    return new Observable<any>((subscriber) => {
      const sub = this.supabase.client
        .channel('listening-messages-update-other-' + chatId)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'Messages',
            filter: `chatId=eq.${chatId}`,
          },
          (payload) => {
            subscriber.next(payload.new as Message);
          }
        )
        .subscribe();
      return function unsubscribe() {
        sub.unsubscribe();
      };
    }).pipe(
      filter((m) => m.userId !== this.currentUser()),
      switchMap((m) => this.getMessage(m.id) as Observable<Message>)
    );
  }

  listenUpdateMyMessages(chatId: number) {
    return new Observable<any>((subscriber) => {
      const sub = this.supabase.client
        .channel('listening-messages-update-me-' + chatId)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'Messages',
            filter: `chatId=eq.${chatId}`,
          },
          (payload) => {
            subscriber.next(payload.new as Message);
          }
        )
        .subscribe();
      return function unsubscribe() {
        sub.unsubscribe();
      };
    }).pipe(
      filter((m) => m.userId === this.currentUser()),
      switchMap((m) => this.getMessage(m.id) as Observable<Message>)
    );
  }
}
